import React from 'react'
import {
  Select,
  MenuItem,
  Radio,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Typography,
  useTheme,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { onFilterDropDownChange } from './helpers'

export const DesktopFilterDropdown = ({ filterDefinition, options, onChange, checked, styles }) => {
  checked = checked || []
  let disabledProps = {}
  if (options.length === 0) {
    disabledProps = {
      disabled: true,
    }
  }

  const theme = useTheme()
  const isMultiple = filterDefinition.isMultiple

  const handleDelete = valueToRemove => event => {
    event.stopPropagation() // Ensure the event doesn't bubble up and open the dropdown
    const newChecked = checked.filter(item => item.name !== valueToRemove)
    onChange({
      filterDefinition,
      selectedValues: newChecked,
    })
  }

  const selectedValues = checked.map(item => item.name)
  const selectValue = isMultiple ? selectedValues : selectedValues[0] || ''

  // Conditional rendering for `isYesNo` filters
  if (filterDefinition.isYesNo) {
    const isChecked = checked.some(item => item.value === '1')

    return (
      <FormControl
        sx={{ m: 1, width: 150, display: 'flex', flexDirection: 'row', alignItems: 'center' }}
      >
        <Checkbox
          checked={isChecked}
          onChange={event => {
            const selectedValues = event.target.checked ? [{ value: '1', name: 'Yes' }] : [] // Clear filter when unchecked
            onChange({
              filterDefinition,
              selectedValues,
            })
          }}
          color='default'
          sx={{
            color: 'white',
            '&.Mui-checked': {
              color: 'white', // White checkmark
            },
            marginRight: 1,
          }}
        />
        <Typography
          sx={{
            color: 'white',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {filterDefinition.displayName}
        </Typography>
      </FormControl>
    )
  }

  // Default dropdown rendering
  return (
    <FormControl sx={{ m: 1, width: 200 }} size='small' {...disabledProps}>
      <InputLabel
        sx={{
          fontSize: '.7rem', // Match CompositeFilterDropdown label font size
          color: theme.palette.primary.dark,
          '&.MuiInputLabel-shrink': {
            color: theme.palette.primary.dark, // Dark color when shrunk
          },
        }}
        id={`label=${filterDefinition.name}`}
      >
        {filterDefinition.displayName}
      </InputLabel>
      <Select
        labelId={`label-${filterDefinition.name}`}
        id={filterDefinition.name}
        value={selectValue}
        multiple={isMultiple}
        sx={{
          minHeight: '32px', // Base height to match CompositeFilterDropdown
          display: 'flex',
          alignItems: 'flex-start', // Allow content to grow vertically
          flexWrap: 'wrap', // Wrap chips when there are multiple
          gap: 0.5,
          padding: 0,
          '& .MuiSelect-select': {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            padding: '4px 8px',
            minHeight: '32px', // Ensure default compact height
            maxHeight: '72px', // Limit content height
            overflowY: 'auto', // Allow scrolling when content exceeds max height
          },
        }}
        displayEmpty
        renderValue={selected =>
          isMultiple ? (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                maxHeight: '72px', // Limit height for multiple chips
                overflow: 'hidden', // Hide overflow content
              }}
            >
              {selected.map(value => (
                <Chip
                  sx={{
                    fontSize: '.7rem',
                    height: '24px',
                    maxWidth: '150px', // Prevent chips from getting too wide
                    '& .MuiChip-label': {
                      fontSize: '.7rem',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      maxWidth: '100px', // Limit label width
                    },
                    '& .MuiChip-deleteIcon': {
                      fontSize: '16px', // Make delete icon more visible
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.error.main,
                      },
                    },
                  }}
                  key={value}
                  label={value}
                  onDelete={handleDelete(value)}
                  deleteIcon={<ClearIcon sx={{ fontSize: '16px' }} />}
                  onMouseDown={e => e.stopPropagation()} // Prevent the dropdown from opening on delete icon click
                />
              ))}
            </Box>
          ) : (
            <Typography 
              sx={{ 
                color: theme.palette.primary.dark, 
                fontSize: '.7rem',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {selected}
            </Typography>
          )
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300, // Limit dropdown height
              overflow: 'auto', // Allow scrolling in dropdown
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
        onChange={event => onFilterDropDownChange(event, options, filterDefinition, onChange)}
      >
        {options.map(({ name, value }) => (
          <MenuItem
            classes={{
              root: styles?.menuItem,
            }}
            name={name}
            key={`${value}-${name}`}
            value={name}
            id={value}
          >
            {!isMultiple ? (
              <Radio checked={checked.some(item => item.name === name)} />
            ) : (
              <Checkbox checked={checked.some(item => item.name === name)} />
            )}
            <ListItemText
              primary={
                <Typography
                  sx={{
                    fontSize: '.8rem',
                    color: theme.palette.primary.dark,
                  }}
                >
                  {name}
                </Typography>
              }
            />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}